import { useContext, useEffect, useRef, useState } from 'react'
import './index.less'
import { Input, InputRef } from 'antd'
import { CmdContext, EngineContext } from '@/App';
import { ReactComponent as ConsoleSvg } from './icons/console.svg'
import { ReactComponent as ArrowUpSvg } from './icons/arrow-up.svg'
import classnames from 'classnames';
import { InputOpitonFormat, NeedInputEnum, OutputDataFormat, OutputDataType, PythaEventAngle, PythaEventCMD, PythaEventConfirm, PythaEventCount, PythaEventName, PythaEventNeedInput, PythaEventNumber, PythaEventObject, PythaEventOption, PythaEventPointerDown, PythaEventString, PythaEventText } from 'pytha2';
import { BaseUtils, Point } from 'tnbase';
import classNames from 'classnames';

/**
 * 父组件传递属性
 * - `inputRef`: 获取输入框 DOM 元素的引用
 */
interface Iprops {
}

/**
 * 命令输入区
 * @param props 
 * @returns 
 */
export default function StmodeInput(props: Iprops) {

    const { cmdInputDisabled, updateCmdInputValue } = useContext(CmdContext);

    /**
     * 输入框引用，用于聚焦快捷键操作。
     */
    const inputRef = useRef<InputRef>();
    const { engine, cmdValue, setCmdValue, emitCommand } = useContext(EngineContext); // 获取上下文

    /**
     * 筛选后的命令列表
     */
    const [filteredCmdList, setFilteredCmdList] = useState<{ cmd: string; desciption: string }[]>([]);
    /**
     * 输入CMD命令时，用于高亮提示当前候选的命令索引
     */
    const [activeCandidateCmdIndex, setActiveCandidateCmdIndex] = useState(0);

    /**
     * 引擎向系统声明 请求的下一步输入的数据类型
     */
    const [inputTypes, setInputypes] = useState<string[]>([]);

    /**
     * 命令过程中的可选项列表
     */
    const [cmdOptions, setCmdOptions] = useState<InputOpitonFormat[]>([]);
    /**
     * 命令过程中的可选项列表的ref
     */
    const cmdOptionsRef = useRef(cmdOptions);
    /**
     * 当前在输入框前部的日志
     */
    const [currentLog, setCurrentLog] = useState<string>("");
    /**
     * 当前在输入框前部的日志的ref
     */
    const currentLogRef = useRef(currentLog);
    /**
     * 消息日志列表
     */
    const [logs, setLogs] = useState<string[]>([]);
    /**
     * 消息日志列表的ref
     */
    const logsRef = useRef(logs);
    /**
     * 是否显示消息日志
     */
    const [logVisible, setLogVisible] = useState<boolean>(false);

    /**
     * 强制触发页面渲染
     */
    const [, setUpdate] = useState<any>();
    /**
     * 控制日志淡入淡出
     */
    const [fadeOut, setFadeOut] = useState(false);
    /**
     * 日志上次变化的时间
     */
    const [lastChangeTime, setLastChangeTime] = useState(Date.now());

    /**
     * 用于倒计时隐藏提示框的计时器
     */
    let timer;

    const [dyActive, setDyActive] = useState<boolean>(false);

    useEffect(() => {
        logsRef.current = logs;
    }, [logs])

    useEffect(() => {
        currentLogRef.current = currentLog;
    }, [currentLog])

    useEffect(() => {
        cmdOptionsRef.current = cmdOptions;
    }, [cmdOptions])

    useEffect(() => {
        // 更新最后变化时间
        setLastChangeTime(Date.now());
        // 瞬间出现
        setFadeOut(false);
    }, [logs]);

    useEffect(() => {
        // 检查是否需要开始淡显
        const interval = setInterval(() => {
            // 超过10秒未变化，开始淡出
            if (Date.now() - lastChangeTime > 10000) {
                setFadeOut(true);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [lastChangeTime]);

    /**
     * 初始化引擎后，监听引擎的输出
     */
    useEffect(() => {
        if (!engine) return;


        const unsubscribe = engine?.eventMediator?.noticeHandler?.onNotice?.subscribe((sender, event) => {
            const cmd = event.cmd;
            if (cmd === PythaEventName.NEED_INPUT) {
                let data: InputOpitonFormat[] = event.data || [];
                let notice = data.find(item => item.type === NeedInputEnum.ACTIVE);
                // 这个notice得放在最前面push，防止cmdoptions覆盖
                if (notice != null) {
                    if (notice?.description) {
                        pushSystemLog(notice.description);
                    }
                    let inputTypes = notice.inputTypes || [];
                    setInputypes(inputTypes);
                }
                let options = data.filter(item => item.type === NeedInputEnum.OPTION);
                setCmdOptions(options);
                cmdOptionsRef.current = options;
                if (notice?.inputTypes?.includes(PythaEventName.DY_TEXT)) {
                    setDyActive(true);
                } else {
                    updateCmdInputValue(false);
                }
            }
            if (event.cmd === PythaEventName.NOTICE) {
                pushSystemLog(`${event.data}`);
            }
            if (event.cmd === PythaEventName.OUTPUT_DATA) {
                let data: OutputDataFormat = event.data;
                if (data.type === OutputDataType.STRING) {
                    pushInputLog(`${data.data}`, true, false);
                } else if (data.type === OutputDataType.NUMBER) {
                    // TODO 处理数字类型的输出
                } else if (data.type === OutputDataType.POINT) {
                    // TODO 处理点的输出
                }
            }
        })
        return () => {
            unsubscribe?.();
        }
    }, [engine])

    useEffect(() => {
        let domElement = engine?.storage?.viewportStore?.renderer?.domElement
        if (domElement) {
            domElement.addEventListener("pointerdown", onPointerDown);
            domElement.addEventListener('keydown', onKeyUp);
        }
        return () => {
            if (domElement) {
                domElement.removeEventListener("pointerdown", onPointerDown);
                domElement.removeEventListener('keydown', onKeyUp);
            }
        }
    }, [engine, filteredCmdList, currentLog, logs, cmdOptions, dyActive, cmdValue, activeCandidateCmdIndex])

    const onPointerDown = (e) => {
        if (!engine) return
        if (engine.eventMediator.tempActiveGraph) return
        if (2 != e.button) return
        // 执行高亮命令
        const selectedCommand = filteredCmdList[activeCandidateCmdIndex]?.cmd || cmdValue;
        if (selectedCommand.length > 0) {
            pushInputLog(`${selectedCommand}`)
            setTimeout(() => {
                emitCommand(selectedCommand); // 发送命令 settimeout 解除systemlog的闭包问题
            }, 0);
        } else {
            // 判断是重复上一次命令
            if (engine.eventMediator.lastCmd != null) {
                let cmd = new PythaEventCMD();
                cmd.data = engine.eventMediator.lastCmd;
                engine.eventMediator?.emitEvent(cmd);
            }
        }
        setFilteredCmdList([]); // 隐藏命令列表
    }

    /**
     * 系统消息存入日志 需要在新的cmdoption覆盖进去之前调用，以便被拉入log记录
     * @param log 日志
     * @param inputStr 用户键入的指令
     */
    const pushSystemLog = (log: string = "") => {
        let logs = logsRef.current;
        let currentLog = currentLogRef.current;
        let cmdOptions = cmdOptionsRef.current;
        if (logs.length > 20) {
            logs.splice(0, 1);
        }
        // 如果之前有留存的当前输入框内的记录，需要拼接后存入记录
        let current = currentLog;
        let newLogs = [...logs];
        if (current.length > 0) {
            // 生成日志格式
            for (let option of cmdOptions) {
                let value = `[${option.description}(${option.option})]`;
                current = `${current} ${value}`
            }
            current = `${current}: `
            // 将旧的日志存入序列
            newLogs = [...newLogs, current];
            setCurrentLog("");
            currentLogRef.current = "";
            setCmdOptions([]);
            cmdOptionsRef.current = [];
        }
        if (log.includes("结束")) {
            setCurrentLog("");
            currentLogRef.current = "";
            setInputypes([]);
            setCmdOptions([]);
            cmdOptionsRef.current = [];
            setFilteredCmdList([]); // 隐藏命令列表
            // setLogs([...newLogs, log]);
            // logsRef.current = [...newLogs, log];
            setCmdValue("");
            setDyActive(false);
            // inputRef.current?.blur();
            let domElement = engine?.storage?.viewportStore?.renderer?.domElement;
            domElement?.focus();
            return;
        }
        if (engine.isCmdActive) {
            setCurrentLog(`${log}`);
            currentLogRef.current = `${log}`;
            setLogs([...newLogs]);
            logsRef.current = [...newLogs];
        } else {
            setLogs([...newLogs, log]);
            logsRef.current = [...newLogs, log];
        }
        setCmdValue("");
    }

    /**
     * 用户键入指令时存储日志
     * @param inputStr 
     * @param clearCurrentTip 是否清空提醒
     * @param spliceCurrentLog 是否将留存内容拼接输出 
     */
    const pushInputLog = (inputStr: string = "", clearCurrentTip: boolean = true, spliceCurrentLog: boolean = true) => {
        let logs = logsRef.current;
        let currentLog = currentLogRef.current;
        let cmdOptions = cmdOptionsRef.current;
        if (currentLog.length > 0 && spliceCurrentLog) {
            // 如果之前有留存的当前输入框内的记录，需要拼接后存入记录
            // 生成日志格式
            let current = currentLog;
            for (let option of cmdOptions) {
                let value = `[${option.description}(${option.option})]`;
                current = `${current} ${value}`
            }
            current = `${current}: `
            if (inputStr.length > 0) {
                current = `${current}${inputStr}`;

            }
            // 将旧的日志存入序列
            setLogs([...logs, current]);
            logsRef.current = [...logs, current];
        } else {
            setLogs([...logs, inputStr]);
            logsRef.current = [...logs, inputStr];
        }
        if (clearCurrentTip) {
            setCurrentLog("");
            currentLogRef.current = "";
            setCmdOptions([]);
            cmdOptionsRef.current = [];
        }
        setCmdValue("");
    }

    /**
     * 监听键盘事件处理输入框聚焦
     */
    useEffect(() => {
        if (!engine) return;
        let domElement = engine?.storage?.viewportStore?.renderer?.domElement;
        if (domElement == null) return;
        // let viewPanel = document.getElementById('view-panel');
        // if (viewPanel == null) return;
        domElement.addEventListener('keydown', handleKeyDown);
        return () => {
            domElement.removeEventListener('keydown', handleKeyDown);
        };
    }, [engine, dyActive])

    /**
     * 处理输入框聚焦
     * @param event 
     * @returns 
     */
    const handleKeyDown = (event) => {
        //按住ctrl，聚焦到图形区
        if (event.ctrlKey || event.shiftKey) return;
        if (cmdInputDisabled) return;
        if (
            (event.code >= 'KeyA' && event.code <= 'KeyZ')  // 字母键
            || (event.code >= 'Digit0' && event.code <= 'Digit9') // 数字键
            || (event.code >= 'Numpad0' && event.code <= 'Numpad9')  // 小键盘数字键
            || event.code === 'NumpadDecimal'  // 小键盘小数点
            || event.key === '-' // 负号
        ) {
            setUpdate(BaseUtils.genUUID());
            inputRef.current?.focus(); // 聚焦输入框
        }
    };

    /**
     * 筛选命令
     * @param value 输入框的值
     */
    const filterCommands = (value: string) => {
        if (!engine) return; // 确保 engine 存在
        const allCommands = engine.storage.registerStore.cmdList || [];
        const filtered = allCommands.filter((cmd: { cmd: string; desciption?: string }) =>
            cmd.cmd.toLowerCase().startsWith(value.toLowerCase())
        );
        setFilteredCmdList(filtered);
        setActiveCandidateCmdIndex(0); // 默认高亮第一项
    };

    /**
     * 输入框变化事件
     * @param e 输入框事件对象
     */
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        value = value.toUpperCase().trim();
        setCmdValue(value);
        if (!engine?.eventMediator?.tempActiveGraph) {
            filterCommands(value); // 实时筛选
        }
    };
    /**
     * 键盘命令识别
     * @param e 键盘事件对象
     */
    const onKeyUp = (e) => {
        const code = e.code;
        if (dyActive) return;
        if (e.ctrlKey || e.shiftKey) return;
        if (code === "Escape") {
            setCmdValue("");
            setCurrentLog("");
            currentLogRef.current = "";
            setInputypes([]);
            setCmdOptions([]);
            cmdOptionsRef.current = [];
            setFilteredCmdList([]); // 隐藏命令列表
            // inputRef.current?.blur();
            let domElement = engine?.storage?.viewportStore?.renderer?.domElement;
            domElement?.focus();
            return;
        }
        if (code === "ArrowDown" && filteredCmdList.length > 0) {
            // 向下移动高亮
            setActiveCandidateCmdIndex((prev) => (prev + 1) % filteredCmdList.length);
            return;
        }
        if (code === "ArrowUp" && filteredCmdList.length > 0) {
            // 向上移动高亮
            setActiveCandidateCmdIndex((prev) => (prev - 1 + filteredCmdList.length) % filteredCmdList.length);
            return;
        }
        if (code === "Enter" || code === "NumpadEnter" || code === "Space") {
            // 执行命令输入
            if (!engine) {
                setCmdValue("");
                return;
            }
            inputRef.current?.blur();
            let domElement = engine?.storage?.viewportStore?.renderer?.domElement;
            if (domElement != null) {
                domElement.focus(); // 这样可以让下一次直接输入聚焦命令框
            }
            // 判断：如果没有激活的图，发CMD
            if (!engine.isCmdActive) {
                // 执行高亮命令
                const selectedCommand = filteredCmdList[activeCandidateCmdIndex]?.cmd || cmdValue;
                if (selectedCommand.length > 0) {
                    pushInputLog(`${selectedCommand}`)
                    setTimeout(() => {
                        emitCommand(selectedCommand); // 发送命令 settimeout 解除systemlog的闭包问题
                    }, 0);
                }
                // setCmdValue(""); // 清空输入框
                setFilteredCmdList([]); // 隐藏命令列表

                return;
            }
            if (/[a-zA-Z]/.test(cmdValue) && cmdOptions.length > 0) {
                // 判断输入了字母，可能是选项 约定选项必须是字母
                let isMatch = false;
                if (cmdOptions.map(option => option.option.toUpperCase()).includes(cmdValue.toUpperCase())) {
                    // 匹配选项
                    isMatch = true;
                }
                if (isMatch) {
                    pushInputLog(`${cmdValue}`, isMatch);
                    // 发一个选项信号
                    let pythaEvent = new PythaEventOption();
                    pythaEvent.data = cmdValue
                    engine.eventMediator?.emitEvent(pythaEvent);
                    return;
                } else if (inputTypes?.some(e => e === PythaEventName.TEXT)) {
                    // 匹配输入文字
                    // 发一个文字信号
                    let pythaEvent = new PythaEventText();
                    pythaEvent.data = cmdValue;
                    pushInputLog(`${pythaEvent.data}`)
                    engine.eventMediator?.emitEvent(pythaEvent);
                    return;
                } else {
                    pushInputLog(`无此选项`, false);
                    return;
                }
            }

            // 如果有激活的图，根据（通知类型？）发不同类型的数据
            console.log('inputTypes', inputTypes)
            if (inputTypes.length <= 0) {
                engine.eventMediator?.emitEvent(new PythaEventConfirm());
                return;
            }

            let enums: string[] = inputTypes;
            let havePoint = enums?.some(e => e === PythaEventName.POINTER_DOWN);
            let haveComma = cmdValue?.includes("，") || cmdValue?.includes(",");
            if (havePoint && haveComma) {
                // 存在逗号，并且需要点，将这个字符串构造成点的形式
                let result = [0, 0, 0];
                const parts = cmdValue.split(/,|，/);  //处理中文逗号
                // 遍历分割的数组，保留数字部分，异常部分置为 0
                for (let i = 0; i < 3; i++) {
                    const value = parts[i]?.trim();
                    if (value) {
                        result[i] = Number(value);
                    } else {
                        result[i] = 0;
                    }
                }
                // 发一个点的信号
                let pythaEvent = new PythaEventPointerDown();
                let pointer = pythaEvent.data;
                pointer.point2 = undefined;
                pointer.point = new Point(result[0], result[1], result[2]);
                pointer.type = "SPECIFY";
                pointer.ctrlKey = false;
                pointer.shiftKey = false;
                pointer.altKey = false;
                pointer.button = -1;
                pointer.id = 100001;
                pointer.deltaY = 0;
                pushInputLog(`${result[0]},${result[1]},${result[2]}`)
                engine.eventMediator?.emitEvent(pythaEvent);
                return;
            }
            // 根据需要发不同的信号
            let anotherEnums = enums?.filter(item => item !== PythaEventName.POINTER_DOWN);
            if (anotherEnums?.length === 0 && havePoint) {
                // 说明只有点，但是没有逗号，构造成纯数字即可
                let value: any = BaseUtils.FloatVerification(cmdValue);
                if (!value) {
                    engine.eventMediator?.emitEvent(new PythaEventConfirm());
                    return;
                }
                // 发一个点的信号
                let pythaEvent = new PythaEventPointerDown();
                let pointer = pythaEvent.data;
                pointer.point2 = undefined;
                pointer.type = "SPECIFY";
                pointer.point = new Point(value, 0, 0);
                pointer.ctrlKey = false;
                pointer.shiftKey = false;
                pointer.altKey = false;
                pointer.button = -1;
                pointer.id = 100001;
                pointer.deltaY = 0;
                pushInputLog(`${value}`);
                engine.eventMediator?.emitEvent(pythaEvent);
                return;
            }
            // 除了点的另外一项
            if (anotherEnums.includes(PythaEventName.NUMBER)) {
                let value: any = BaseUtils.FloatVerification(cmdValue);
                if (!value) {
                    // 发一个确定信号
                    engine.eventMediator?.emitEvent(new PythaEventConfirm());
                    return;
                    // value = 0;
                }
                // 发一个数字信号
                let pythaEvent = new PythaEventNumber();
                pythaEvent.data = Number(value) || 0;
                pushInputLog(`${pythaEvent.data}`)
                engine.eventMediator?.emitEvent(pythaEvent);
            } else if (anotherEnums.includes(PythaEventName.ANGLE)) {
                let value: any = BaseUtils.FloatVerification(cmdValue);
                if (!value) {
                    // 发一个确定信号
                    engine.eventMediator?.emitEvent(new PythaEventConfirm());
                    return;
                }
                // 发一个角度信号
                let pythaEvent = new PythaEventAngle();
                pythaEvent.data = Number(value) || 0;
                pushInputLog(`${pythaEvent.data}`)
                engine.eventMediator?.emitEvent(pythaEvent);
            } else if (anotherEnums.includes(PythaEventName.COUNT)) {
                // 发一个数字信号
                let value: any = BaseUtils.FloatVerification(cmdValue);
                if (!value) {
                    // 发一个确定信号
                    engine.eventMediator?.emitEvent(new PythaEventConfirm());
                    return;
                }
                value = Number(value);
                let pythaEvent = new PythaEventCount();
                pythaEvent.data = Number(value) || 0;
                pushInputLog(`${pythaEvent.data}`)
                engine.eventMediator?.emitEvent(pythaEvent);
            } else if (anotherEnums.includes(PythaEventName.TEXT)) {
                // 发一个文字信号
                let pythaEvent = new PythaEventText();
                pythaEvent.data = cmdValue;
                pushInputLog(`${pythaEvent.data}`)
                engine.eventMediator?.emitEvent(pythaEvent);
            } else if (anotherEnums.includes(PythaEventName.CONFIRM)) {
                // 发一个确定信号
                engine.eventMediator?.emitEvent(new PythaEventConfirm());
            } else if (anotherEnums.includes(PythaEventName.STRING)) {
                let value: string = cmdValue;
                if (!value) {
                    // 发一个确定信号
                    engine.eventMediator?.emitEvent(new PythaEventConfirm());
                    return;
                }
                // 发一个角度信号
                let pythaEvent = new PythaEventString();
                pythaEvent.data = value;
                pushInputLog(`${pythaEvent.data}`)
                engine.eventMediator?.emitEvent(pythaEvent);
            } else if (anotherEnums.includes(PythaEventName.OBJECT)) {
                let value: string = cmdValue;
                if (!value) {
                    // 发一个确定信号
                    engine.eventMediator?.emitEvent(new PythaEventConfirm());
                    return;
                }
                // 发一个对象信号
                let pythaEvent = new PythaEventObject();
                pythaEvent.data = value;
                pushInputLog(`${pythaEvent.data}`)
                engine.eventMediator?.emitEvent(pythaEvent);
            }
        }
    };
    /**
     * 点击命令列表项
     * @param cmd 点击的命令
     */
    const onCmdClick = (cmd: string) => {
        pushInputLog(`${cmd}`)
        emitCommand(cmd);
        setCmdValue("");
        setFilteredCmdList([]);
    };

    /**
     * 点击命令选项
     * @param option 
     */
    const onOptionClick = (option: InputOpitonFormat) => {
        // 发一个选项信号
        let pythaEvent = new PythaEventOption();
        pythaEvent.data = option.option;
        pushInputLog(`${option.option}`)
        engine.eventMediator?.emitEvent(pythaEvent);
    }


    return (
        <div className="static-input-container">
            {/* 命令输入框 */}
            <Input
                onKeyDown={(e) => onKeyUp(e)}
                ref={inputRef} value={cmdValue} disabled={dyActive} lang="en" onChange={onChange} placeholder="键入命令" autoComplete="off"
                suffix={
                    <ArrowUpSvg style={{ cursor: 'pointer' }} onClick={() => { }} />}
                prefix={
                    <div className="static-prefix">
                        <ConsoleSvg />
                        {engine?.getActiveCmdStr() != null && (
                            <div className="tip-cmd">{engine?.getActiveCmdStr()}</div>
                        )}
                        <div>{currentLog}</div>
                        <div className={classnames({
                            "cmd-options": true,
                            "active": cmdOptions.length > 0
                        })}>
                            {cmdOptions.map((option: InputOpitonFormat, idx) => (
                                <div className="cmd-options-item" key={idx} onClick={() => onOptionClick(option)}>
                                    <span>{option.description}</span>
                                    (<span className="key">{option.option}</span>)
                                </div>
                            ))}
                        </div>
                    </div>
                } />
            <div
                className={classnames({
                    "stmode-command-list": true,
                    visible: filteredCmdList.length > 0,
                })}
            >
                {filteredCmdList.map((cmd, idx) => (
                    <div
                        key={idx}
                        className={classnames({
                            "cmd-item": true,
                            active: idx === activeCandidateCmdIndex,
                        })}
                        onClick={() => onCmdClick(cmd.cmd)}
                    >
                        <span>{cmd.cmd}</span>
                        {cmd.desciption && <span className="cmd-alias"> ({cmd.desciption}) </span>}

                    </div>
                ))}
            </div>
            {/* 日志显示 */}
            {logs.slice(-3).reverse().map((log, idx) => {
                return (
                    <div key={idx}
                        className={`log-history-tag log-history-tag-${idx} ${fadeOut ? 'hidden' : 'visiable'}`}>
                        {log}
                    </div>
                )
            })}
        </div>
    );
}